import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { pointPath } from "../lib/pointing";
import { previewFit, type PreviewSize } from "../lib/previewSize";
import { urlInRoot } from "../lib/workspaceState";
import { PageOutlineSkeleton } from "./Skeleton";

/** How long a loaded page has to say it's ready before it counts as an error page. */
const PREVIEW_READY_GRACE_MS = 4_000;
/** How long a page may take to load at all. */
const PREVIEW_LOAD_TIMEOUT_MS = 30_000;

interface PreviewFrameProps {
  /** Isolated preview origin for a saved bundle draft. */
  root: string;
  /** Requested page. Changing `navigation` reloads the frame even for the same path. */
  path: string;
  navigation: number;
  title: string;
  onPathChange: (path: string) => void;
  onReturnHome: () => void;
  banner?: ReactNode;
  tray?: ReactNode;
  hint?: ReactNode;
  tone?: "proposal";
  badge?: string;
  mode?: "off" | "select" | "point" | "edit";
  onPick?: (pick: TextPick) => void;
  onSelect?: (selection: SectionSelection | null) => void;
  onPhoto?: (photo: PhotoPick) => void;
  onSections?: (sections: SectionSummary[]) => void;
  /** Page labels come from the website navigation, which is what its visitors see. */
  onPages?: (pages: Array<{ path: string; label: string }>) => void;
  /** A selection requested from the outline rather than a gesture inside the frame. */
  selectNode?: string | null;
  overrides?: Record<string, string>;
  overlay?: ReactNode;
  commenting?: boolean;
  onDrop?: (drop: CommentDrop) => void;
  pins?: PinView[];
  onPinOpen?: (id: string, at: FrameAnchor) => void;
  /** The page scroll `overlay` was placed at; given, the overlay moves with the page as it scrolls. */
  overlayScroll?: number;
  reveal?: { id: string; nonce: number } | null;
  /** Point to change: select this level of the current path instead (a new nonce each time). */
  pointStep?: { depth: number; nonce: number } | null;
  onMissing?: (pagePath: string, ids: string[]) => void;
  /**
   * Preview only: lays the page out at this screen's width, scaled down to fit. Positions the page
   * reports aren't scaled, so it is for looking at the site, never with editing, pins or popovers.
   */
  size?: PreviewSize;
}

export interface PinAnchor {
  nodeId: string;
  itemId?: string;
  x: number;
  y: number;
}

export interface PinView extends PinAnchor {
  id: string;
  number: number | null;
  state: "new" | "draft" | "sent" | "done" | "failed" | "asked";
  label: string;
  active?: boolean;
  /** The part of the page it points at is gone (after a redesign, say): it waits at the top of the preview. */
  moved?: boolean;
}

/**
 * Where a popover points, in preview-area pixels as the page stood when it was picked. `scrollY` is
 * the page's scroll then, so the popover can follow the page as it scrolls.
 */
export interface FrameAnchor { top: number; bottom: number; left: number; scrollY?: number }

/** One level of what Point to change selected: the section, a list item in it, or its words or photo. */
export interface PointStep { level: "section" | "item" | "text" | "photo" | "slot"; label: string }
/** The levels from the section down, and which one is selected; the owner can step back up them. */
export interface PointPath { steps: PointStep[]; depth: number }

export interface CommentDrop extends PinAnchor, FrameAnchor {
  viewport: "phone" | "desktop";
  path?: PointPath;
  /** Point to change: the content records the selected part shows. */
  records?: string[];
}

export interface TextPick {
  path?: PointPath;
  scrollY?: number;
  bind: string;
  bindingId?: string;
  matches?: Array<{ bind: string; bindingId: string }>;
  text: string;
  top: number;
  bottom: number;
  left: number;
  nodeId?: string;
  itemId?: string;
  x?: number;
  y?: number;
}

export interface SectionSummary { nodeId: string; label: string; binds: string[]; hidden?: boolean; rect: { top: number; bottom: number; left: number; right: number; width: number; height: number }; scrollY?: number }
export interface SectionSelection extends SectionSummary { itemId?: string; x?: number; y?: number }
/** An image the design didn't bind, as `media.bind` names it. */
export interface UnboundPhoto { path: string; src: string; occurrence: number }
/** `slot` is an item with no photo yet: the empty box its design keeps for one was picked. */
export interface PhotoPick extends FrameAnchor { path?: PointPath; nodeId?: string; itemId?: string; x: number; y: number; recordId?: string; unbound?: UnboundPhoto; slot?: string; alt: string; src: string; width?: number; height?: number }

function unboundPhoto(value: unknown): value is UnboundPhoto {
  const candidate = value as Partial<UnboundPhoto> | null;
  return typeof candidate === "object" && candidate !== null && typeof candidate.path === "string" && candidate.path.startsWith("/")
    && typeof candidate.src === "string" && Number.isInteger(candidate.occurrence) && candidate.occurrence! >= 0;
}

const PHONE_WIDTH = 600;
const BRIDGE_CHANNEL = "stackje-preview-v1";

/** Executable bundle preview isolated from the admin document and controlled through its bridge. */
export function PreviewFrame({
  root, path, navigation, title, onPathChange, onReturnHome, banner, tray, hint, tone,
  badge = "Proposed design, not applied", mode = "off", onPick, onSelect, onPhoto, onSections, onPages, selectNode, overrides, overlay,
  commenting = false, onDrop, pins, onPinOpen, overlayScroll, reveal, pointStep, onMissing, size,
}: PreviewFrameProps) {
  const frame = useRef<HTMLIFrameElement>(null);
  const area = useRef<HTMLDivElement>(null);
  // The space the page gets, measured, so a chosen screen size can be scaled into it.
  const stage = useRef<HTMLDivElement>(null);
  const [space, setSpace] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = stage.current;
    if (!element || !size) return;
    const measure = () => setSpace((now) => now.width === element.clientWidth && now.height === element.clientHeight ? now : { width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [size]);
  const fit = size && space.width > 0 ? previewFit(size, space) : null;
  const loadedOnce = useRef(false);
  const pick = useRef(onPick);
  pick.current = onPick;
  const drop = useRef(onDrop);
  drop.current = onDrop;
  const select = useRef(onSelect);
  select.current = onSelect;
  const photo = useRef(onPhoto);
  photo.current = onPhoto;
  const sectionsUpdate = useRef(onSections);
  sectionsUpdate.current = onSections;
  const pagesUpdate = useRef(onPages);
  pagesUpdate.current = onPages;
  const missingReport = useRef(onMissing);
  missingReport.current = onMissing;
  const focusPin = useRef<string | null>(null);
  const revealed = useRef<number | null>(null);
  const selectedNode = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  // The page loaded without the preview bridge saying it's ready (an error page), or never loaded at all.
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const readyWait = useRef<number | null>(null);
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const [leftPreview, setLeftPreview] = useState(false);
  const [bridgeOrigin, setBridgeOrigin] = useState<string | null>(null);
  const trustedBridgeOrigin = useRef<string | null>(null);
  const [layout, setLayout] = useState<{ box: { top: number; left: number; width: number; height: number }; positions: Record<string, { left: number; top: number } | null>; scrollY: number } | null>(null);
  const src = urlInRoot(root, path);

  const postBridge = useCallback((message: Record<string, unknown>) => {
    if (!bridgeOrigin || !frame.current?.contentWindow) return;
    frame.current.contentWindow.postMessage({ channel: BRIDGE_CHANNEL, ...message }, bridgeOrigin === "null" ? "*" : bridgeOrigin);
  }, [bridgeOrigin]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow || !event.data || event.data.channel !== BRIDGE_CHANNEL || typeof event.data.type !== "string") return;
      const trustedOrigin = trustedBridgeOrigin.current;
      if (trustedOrigin && event.origin !== trustedOrigin) return;
      if (!trustedOrigin) {
        if (event.data.type !== "ready" || (event.origin !== "null" && !event.origin.startsWith("https://"))) return;
        trustedBridgeOrigin.current = event.origin;
        setBridgeOrigin(event.origin);
      }
      const frameBox = frame.current?.getBoundingClientRect();
      const areaBox = area.current?.getBoundingClientRect();
      if (!frameBox || !areaBox) return;
      const scrollY = typeof event.data.scrollY === "number" && Number.isFinite(event.data.scrollY) ? event.data.scrollY : 0;
      const path = pointPath(event.data.path);
      const withPath = path ? { path } : {};
      const verticalOffset = frameBox.top - areaBox.top;
      const horizontalOffset = frameBox.left - areaBox.left;
      function toArea<T extends { top: number; left: number }>(box: T): T {
        return {
          ...box,
          top: verticalOffset + box.top,
          ...("bottom" in box && typeof box.bottom === "number" ? { bottom: verticalOffset + box.bottom } : {}),
          left: horizontalOffset + box.left,
          ...( "right" in box && typeof box.right === "number" ? { right: horizontalOffset + box.right } : {}),
        } as T;
      }
      if (event.data.type === "ready") {
        const pagePath = typeof event.data.path === "string" && event.data.path.startsWith("/") ? event.data.path : "/";
        setLoading(false);
        setFailed(false);
        if (readyWait.current !== null) { window.clearTimeout(readyWait.current); readyWait.current = null; }
        setLeftPreview(false);
        loadedOnce.current = true;
        onPathChange(pagePath);
      } else if (event.data.type === "pick" && typeof event.data.bind === "string" && typeof event.data.text === "string" && event.data.rect) {
        const box = event.data.rect as { top: number; bottom: number; left: number };
        pick.current?.({
          bind: event.data.bind,
          ...(typeof event.data.bindingId === "string" ? { bindingId: event.data.bindingId } : {}),
          ...(Array.isArray(event.data.matches) ? { matches: event.data.matches.flatMap((match: unknown) => {
            if (!match || typeof match !== "object") return [];
            const candidate = match as { bind?: unknown; bindingId?: unknown };
            return typeof candidate.bind === "string" && typeof candidate.bindingId === "string" ? [{ bind: candidate.bind, bindingId: candidate.bindingId }] : [];
          }) } : {}),
          text: event.data.text,
          ...toArea(box),
          scrollY,
          ...withPath,
          ...(typeof event.data.nodeId === "string" ? { nodeId: event.data.nodeId } : {}),
          ...(typeof event.data.itemId === "string" ? { itemId: event.data.itemId } : {}),
          ...(typeof event.data.x === "number" ? { x: event.data.x } : {}),
          ...(typeof event.data.y === "number" ? { y: event.data.y } : {}),
        });
      } else if (event.data.type === "drop" && typeof event.data.nodeId === "string" && event.data.point) {
        const point = event.data.point as { top: number; left: number };
        drop.current?.({
          nodeId: event.data.nodeId,
          ...(typeof event.data.itemId === "string" ? { itemId: event.data.itemId } : {}),
          x: typeof event.data.x === "number" ? event.data.x : 0.5,
          y: typeof event.data.y === "number" ? event.data.y : 0.5,
          viewport: frame.current!.clientWidth < PHONE_WIDTH ? "phone" : "desktop",
          ...toArea({ top: point.top, bottom: point.top + 14, left: point.left }),
          scrollY,
          ...withPath,
          ...(Array.isArray(event.data.records) ? { records: event.data.records.filter((id: unknown): id is string => typeof id === "string" && /^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(id)).slice(0, 40) } : {}),
        });
      } else if (event.data.type === "select") {
        if (event.data.nodeId === null) {
          select.current?.(null);
          return;
        }
        if (typeof event.data.nodeId !== "string" || !event.data.rect) return;
        const box = event.data.rect as SectionSummary["rect"];
        select.current?.({
          nodeId: event.data.nodeId,
          label: typeof event.data.label === "string" ? event.data.label : "Section",
          binds: Array.isArray(event.data.binds) ? event.data.binds.filter((bind: unknown): bind is string => typeof bind === "string") : [],
          rect: toArea(box),
          scrollY,
          ...(typeof event.data.itemId === "string" ? { itemId: event.data.itemId } : {}),
          ...(typeof event.data.x === "number" ? { x: event.data.x } : {}),
          ...(typeof event.data.y === "number" ? { y: event.data.y } : {}),
        });
      } else if (event.data.type === "photo" && event.data.rect) {
        const box = event.data.rect as { top: number; bottom: number; left: number; width?: number; height?: number };
        photo.current?.({
          ...(typeof event.data.nodeId === "string" ? { nodeId: event.data.nodeId } : {}),
          ...(typeof event.data.itemId === "string" ? { itemId: event.data.itemId } : {}),
          x: typeof event.data.x === "number" ? event.data.x : 0.5,
          y: typeof event.data.y === "number" ? event.data.y : 0.5,
          recordId: typeof event.data.recordId === "string" ? event.data.recordId : undefined,
          ...(typeof event.data.slot === "string" && event.data.slot ? { slot: event.data.slot } : {}),
          ...(unboundPhoto(event.data.unbound) ? { unbound: event.data.unbound as UnboundPhoto } : {}),
          alt: typeof event.data.alt === "string" ? event.data.alt : "",
          // The preview sends an absolute URL; anything else is from an untrusted page and isn't shown.
          src: typeof event.data.src === "string" && /^https?:\/\//i.test(event.data.src) ? event.data.src : "",
          ...toArea(box),
          scrollY,
          ...withPath,
        });
      } else if (event.data.type === "sections" && Array.isArray(event.data.sections)) {
        sectionsUpdate.current?.((event.data.sections as SectionSummary[]).filter((entry) => !!entry && typeof entry.nodeId === "string").map((entry) => ({ ...entry, hidden: entry.hidden === true, binds: Array.isArray(entry.binds) ? entry.binds.filter((bind): bind is string => typeof bind === "string") : [], rect: toArea(entry.rect), scrollY })));
      } else if (event.data.type === "pages" && Array.isArray(event.data.pages)) {
        pagesUpdate.current?.((event.data.pages as Array<{ path?: unknown; label?: unknown }>).flatMap((entry) => typeof entry.path === "string" && entry.path.startsWith("/") && typeof entry.label === "string" ? [{ path: entry.path, label: entry.label }] : []));
      } else if (event.data.type === "layout" && event.data.positions && typeof event.data.positions === "object") {
        const positions = event.data.positions as Record<string, { left: number; top: number } | null>;
        setLayout({
          box: { top: frameBox.top - areaBox.top, left: frameBox.left - areaBox.left, width: frame.current!.clientWidth, height: frame.current!.clientHeight },
          positions,
          scrollY,
        });
        const pagePath = typeof event.data.path === "string" && event.data.path.startsWith("/") ? event.data.path : null;
        const missing = Array.isArray(event.data.missing) ? event.data.missing.filter((id: unknown): id is string => typeof id === "string") : [];
        if (pagePath) missingReport.current?.(pagePath, missing);
      } else if (event.data.type === "external") {
        setLeftPreview(true);
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [bridgeOrigin, onPathChange]);

  // Compared by value: parents rebuild pins and overrides on every render, and each configure makes
  // the frame report its sections again, which would otherwise loop.
  const configuration = JSON.stringify({ type: "configure", mode, commenting, overrides: overrides ?? {}, pins: pins ?? [] });
  useEffect(() => {
    postBridge(JSON.parse(configuration));
  }, [postBridge, configuration]);

  useEffect(() => {
    if (!selectNode || selectedNode.current === selectNode) return;
    selectedNode.current = selectNode;
    postBridge({ type: "select", nodeId: selectNode });
  }, [postBridge, selectNode]);

  useEffect(() => {
    if (!selectNode) selectedNode.current = null;
  }, [selectNode]);

  const revealNonce = reveal?.nonce;
  const revealId = reveal?.id;
  useEffect(() => {
    if (!bridgeOrigin || !revealId || revealNonce === revealed.current) return;
    revealed.current = revealNonce ?? null;
    focusPin.current = revealId;
    postBridge({ type: "reveal", id: revealId });
  }, [bridgeOrigin, postBridge, revealId, revealNonce]);

  useEffect(() => {
    const id = focusPin.current;
    const button = id ? area.current?.querySelector<HTMLButtonElement>(`[data-pin="${CSS.escape(id)}"]`) : null;
    if (!button) return;
    focusPin.current = null;
    button.focus({ preventScroll: true });
  }, [layout]);

  const stepNonce = pointStep?.nonce;
  const stepDepth = pointStep?.depth;
  useEffect(() => {
    if (stepNonce !== undefined && stepDepth !== undefined) postBridge({ type: "point-depth", depth: stepDepth });
  }, [postBridge, stepNonce, stepDepth]);

  const hasOverlay = Boolean(overlay);
  useEffect(() => {
    if (!hasOverlay) postBridge({ type: "clear" });
  }, [hasOverlay, postBridge]);

  useEffect(() => {
    setLoading(true);
    setFailed(false);
    setLeftPreview(false);
    setBridgeOrigin(null);
    trustedBridgeOrigin.current = null;
    setLayout(null);
    selectedNode.current = null;
    // A page that never finishes loading counts as failed too.
    const never = window.setTimeout(() => setFailed(true), PREVIEW_LOAD_TIMEOUT_MS);
    return () => {
      window.clearTimeout(never);
      if (readyWait.current !== null) { window.clearTimeout(readyWait.current); readyWait.current = null; }
    };
  }, [src, navigation, attempt]);

  /** The frame finished loading: a preview page says it's ready just before or after, an error page never does. */
  const loaded = () => {
    // Pages clicked to inside the frame, and links that leave the website, are past the first load.
    if (loadedOnce.current && !loadingRef.current) return;
    if (readyWait.current !== null) window.clearTimeout(readyWait.current);
    readyWait.current = window.setTimeout(() => { readyWait.current = null; setFailed(true); }, PREVIEW_READY_GRACE_MS);
  };

  const pinList = (pins ?? []).filter((pin) => !pin.moved);
  const movedPins = (pins ?? []).filter((pin) => pin.moved);
  return (
    <div className="bw-frame" data-tone={tone} data-mode={mode} data-commenting={commenting || undefined} ref={area}>
      {leftPreview ? (
        <div className="bw-strip" data-tone="attention" role="status">
          <span>This link goes outside your website.</span>
          <button type="button" className="bw-btn bw-btn--quiet" onClick={onReturnHome}>Back to your website</button>
        </div>
      ) : banner}
      <div className="bw-frame-stage" ref={stage} data-size={size}>
        <iframe
          key={`${src}#${navigation}#${attempt}`}
          ref={frame}
          src={src}
          onLoad={loaded}
          title={tone === "proposal" ? `Proposed design: ${title}` : `Preview of ${title}`}
          sandbox="allow-scripts"
          referrerPolicy="same-origin"
          aria-busy={loading}
          style={fit ? { position: "absolute", top: 0, left: fit.left, width: fit.width, height: fit.height, transform: fit.scale < 1 ? `scale(${fit.scale})` : undefined, transformOrigin: "0 0" } : undefined}
        />
      </div>
      {loading && failed ? (
        <div className="bw-frame-failed" role="alert">
          <p><strong>This page didn’t load.</strong> Your saved work is fine. Try again in a moment.</p>
          <button type="button" className="bw-btn bw-btn--quiet" onClick={() => setAttempt((count) => count + 1)}>Try again</button>
        </div>
      ) : loading && (loadedOnce.current
        ? <div className="bw-frame-progress" role="status"><span className="bw-sr">Loading page</span></div>
        : <PageOutlineSkeleton label="Loading page" />)}
      {layout && pinList.length > 0 && (
        <div className="bw-pins" style={{ top: layout.box.top, left: layout.box.left, width: layout.box.width, height: layout.box.height }}>
          {pinList.map((pin) => {
            const at = layout.positions[pin.id];
            if (!at || at.top < -8 || at.left < -8 || at.top > layout.box.height + 8 || at.left > layout.box.width + 8) return null;
            return (
              <button
                key={pin.id}
                type="button"
                className={pin.id.startsWith("change-") ? "bw-marker" : "bw-pin"}
                data-pin={pin.id}
                data-state={pin.state}
                data-active={pin.active || undefined}
                style={{ left: at.left, top: at.top }}
                aria-label={pin.label}
                onClick={() => onPinOpen?.(pin.id, { top: layout.box.top + at.top - 14, bottom: layout.box.top + at.top + 14, left: layout.box.left + at.left, scrollY: layout.scrollY })}
              >
                <span aria-hidden="true">{pin.number ?? "+"}</span>
              </button>
            );
          })}
        </div>
      )}
      {layout && movedPins.length > 0 && (
        <div className="bw-moved-pins" role="group" aria-label="Comments whose spot on the page has gone" style={{ top: layout.box.top + 10, left: layout.box.left + 10 }}>
          <span aria-hidden="true">Moved</span>
          {movedPins.map((pin) => (
            <button
              key={pin.id}
              type="button"
              className="bw-pin"
              data-pin={pin.id}
              data-state={pin.state}
              data-active={pin.active || undefined}
              aria-label={pin.label}
              onClick={(event) => {
                const button = event.currentTarget.getBoundingClientRect();
                const frameBox = area.current?.getBoundingClientRect();
                if (!frameBox) return;
                onPinOpen?.(pin.id, { top: button.top - frameBox.top, bottom: button.bottom - frameBox.top, left: button.left - frameBox.left + button.width / 2, scrollY: layout.scrollY });
              }}
            >
              <span aria-hidden="true">{pin.number ?? "+"}</span>
            </button>
          ))}
        </div>
      )}
      {tray}
      {hint}
      {overlay && overlayScroll !== undefined && layout
        ? <div className="bw-overlay-follow" style={{ top: overlayScroll - layout.scrollY }}>{overlay}</div>
        : overlay}
      <span className="bw-private">{tone === "proposal" ? badge : "Private preview"}</span>
    </div>
  );
}
